'use strict'

import { useEffect, useState } from 'react'
import axios from 'axios'
import Ajv from 'ajv'
import { has } from 'lodash'
import { config } from './config.js'
import 'regenerator-runtime/runtime.js'

/**
 * Requests the config object schema.
 * @returns {object} - response
 */
async function reqSchema() {
  return await axios({
    method: 'GET',
    url: `${config.STATIC}/schemas/widgets/config_1.6.3.json`,
  })
}

/**
 * Logs the config object validation errors.
 * @param {array} errors - validation errors
 * @returns {Promise<void>}
 */
function throwConfigError(errors = null, index = null) {
  try {
    let formattedErrors = []

    let errDefinitions = {
      documentation:
        'https://github.com/homewise-solutions-inc/developer-guides/',
      undefined:
        '%cConfiguration object `homewise` is not defined. Check the documentation for more information on the configuration object:',
      invalid:
        '%cThe specified configuration object is not valid, please check the errors listed below & try again with a valid configuration object. Check the documentation for more information on the configuration object: ',
    }

    const docs = {
      productTable:
        'https://github.com/homewise-solutions-inc/developer-guides/blob/master/product_table/embed.md',
      banner:
        'https://github.com/homewise-solutions-inc/developer-guides/blob/master/banners/embed.md',
      mortgageApp:
        'https://github.com/homewise-solutions-inc/developer-guides/blob/master/mortgage_app/embed.md',
      affordabilityCalculator:
        'https://github.com/homewise-solutions-inc/developer-guides/blob/master/affordability_calculator/embed.md',
      closingCostsCalculator:
        'https://github.com/homewise-solutions-inc/developer-guides/blob/master/closing_costs_calculator/embed.md',
      mortgagePaymentCalculator:
        'https://github.com/homewise-solutions-inc/developer-guides/blob/master/mortgage_payment_calculator/embed.md',
    }

    if (index !== null) {
      errDefinitions.undefined =
        '%cConfiguration object `homewise` is not defined for the widget at index: ' +
        index +
        '. Check the documentation for more information on the configuration object: '
      errDefinitions.invalid =
        '%cThe specified configuration object is not valid for the widget at index: ' +
        index +
        ', please check the errors listed below & try again with a valid configuration object. Check the documentation for more information on the configuration object: '
    }

    if (typeof errors !== 'object') {
      console.log(
        errDefinitions.undefined + errDefinitions.documentation,
        'color: red; font-size: 20px'
      )
      return
    }

    errors.forEach((error) => {
      if (error?.dataPath?.toString()?.split('/').includes('banner'))
        errDefinitions.documentation = docs.banner
      else if (error?.dataPath?.toString()?.split('/').includes('productTable'))
        errDefinitions.documentation = docs.productTable
      else if (error?.dataPath?.toString()?.split('/').includes('mortgageApp'))
        errDefinitions.documentation = docs.mortgageApp
      else if (
        error?.dataPath
          ?.toString()
          ?.split('/')
          .includes('mortgagePaymentCalculator')
      )
        errDefinitions.documentation = docs.mortgagePaymentCalculator
      else if (
        error?.dataPath
          ?.toString()
          ?.split('/')
          .includes('closingCostsCalculator')
      )
        errDefinitions.documentation = docs.closingCostsCalculator
      else if (
        error?.dataPath
          ?.toString()
          ?.split('/')
          .includes('affordabilityCalculator')
      )
        errDefinitions.documentation = docs.affordabilityCalculator
      else
        errDefinitions.documentation =
          'https://github.com/homewise-solutions-inc/developer-guides/'
    })

    console.log(
      errDefinitions.invalid + errDefinitions.documentation,
      'color: red; font-size: 20px'
    )

    if (errors && errors.length > 0) {
      errors.forEach((error) => {
        formattedErrors.push({
          property: error.dataPath,
          message: error.message,
          params: error.params,
        })
      })

      console.table(formattedErrors)
    }

    return
  } catch (e) {
    console.error('error at throwConfigError', e)
  }
}

/**
 * Validates the config object using the json-draft-07 schema.
 * @param {Object|Boolean} schema
 * @param {Object} data
 * @returns {Object} { status: Boolean, errors: Array }
 */
function validate(schema = false, data, index) {
  if (!schema) return { status: true }

  const ajv = new Ajv({
    allErrors: true,
    jsonPointers: true,
    verbose: true,
  })

  let validate = ajv.compile(schema),
    valid = validate(data)

  if (!valid) {
    throwConfigError(validate.errors, index)
    return { status: false, errors: validate.errors }
  }

  return { status: true }
}

/**
 * Consumer config custom hook.
 * @param {Object} data - public variable `window.homewise`
 * @returns {Object} - { bg: String, link: String, province: String, intro: Boolean }
 */
export const useConsumerConfig = (data, internal = false, index = null) => {
  const [schema, setSchema] = useState({})

  /** The default config for product table. */
  const productTableConfig = {
    bg: '#FFF',
    intro: true,
    font: true,
    cta: 'Get This Mortgage',
    link: config?.APP,
    rental: 'no',
    mortgageType: 'purchase',
    province: 'ON',
    rateType: 'fixed',
    price: 9e5,
    downPayment: 2e5,
    balance: 13e4,
    lenderType: 'any',
    term: 'any',
    creditScore: '650_900',
    hide: [],
    featureCount: 3,
    lenderName: [],
    style: 'table',
    linkParameters: {},
    linkFragments: {},
    accreditation: true,
    productName: true,
  }

  /** The default config for banner widget. */
  const bannerConfig = {
    style: 'homewise_1',
    bg: '#FFF',
    primaryColor: '#147bc9',
    font: true,
    cta: 'See Your Mortgage Options',
    body: "<p>Complete a brief questionnaire to find your best mortgage option from over 30 lenders.</p><p>Once selected, you'll be connected with a personal advisor to guide you throughout the process.</p>",
    link: config?.APP,
  }

  /** The default config for mortgage app. */
  const mortgageAppConfig = {
    bg: '#FFF',
  }

  /** The default config for Mortgage Payment Calculator. */
  const mortgagePaymentCalculatorConfig = {
    bg: internal ? '#f5f7fa' : '#FFF',
    primaryColor: '#147bc9',
    internal: internal,
  }

  /** The default config for Affordability Calculator. */
  const affordabilityCalculatorConfig = {
    bg: internal ? '#f5f7fa' : '#FFF',
    primaryColor: '#147bc9',
    internal: internal,
  }

  /** The default config for Closing Costs Calculator. */
  const closingCostsCalculator = {
    bg: internal ? '#f5f7fa' : '#FFF',
    primaryColor: '#147bc9',
    internal: internal,
  }

  /** The default config for Rental Offset Calculator. */
  const rentalOffsetCalculator = {
    bg: internal ? '#f5f7fa' : '#FFF',
    primaryColor: '#147bc9',
    internal: internal,
  }

  /** The default main config object. */
  const defaultConfig = {
    productTable: { ...productTableConfig },
    banner: { ...bannerConfig },
    mortgageApp: { ...mortgageAppConfig },
    mortgagePaymentCalculator: { ...mortgagePaymentCalculatorConfig },
    affordabilityCalculator: { ...affordabilityCalculatorConfig },
    closingCostsCalculator: { ...closingCostsCalculator },
    rentalOffsetCalculator: { ...rentalOffsetCalculator },
  }

  if (internal) return defaultConfig

  if (!data) {
    throwConfigError(null, index)
    return defaultConfig
  }

  /** Config object property mapping to incoming values from params for product table. */
  const {
    primaryColor = productTableConfig.primaryColor,
    font = productTableConfig.font,
    intro = productTableConfig.intro,
    bg = productTableConfig.bg,
    cta = productTableConfig.cta,
    link,
    rental = productTableConfig.rental,
    mortgageType = productTableConfig.mortgageType,
    rateType = productTableConfig.rateType,
    province = productTableConfig.province,
    price,
    downPayment,
    balance,
    lenderType = productTableConfig.lenderType,
    term = productTableConfig.term,
    creditScore = productTableConfig.creditScore,
    hide = productTableConfig.hide,
    featureCount = productTableConfig.featureCount,
    lenderName = productTableConfig.lenderName,
    style: resultsStyle = productTableConfig.style,
    linkParameters = productTableConfig.linkParameters,
    linkFragments = productTableConfig.linkFragments,
    accreditation = productTableConfig.accreditation,
    productName = productTableConfig.productName,
  } = !data?.productTable ? productTableConfig : data?.productTable

  /** Config object property mapping to incoming values from params for Mortgage Payment Calculator. */
  const {
    bg: bg2 = mortgagePaymentCalculatorConfig.bg,
    primaryColor: primaryColor2 = mortgagePaymentCalculatorConfig.primaryColor,
  } = !data?.mortgagePaymentCalculator
    ? mortgagePaymentCalculatorConfig
    : data?.mortgagePaymentCalculator

  /** Config object property mapping to incoming values from params for Affordability Calculator. */
  const {
    bg: bg3 = affordabilityCalculatorConfig.bg,
    primaryColor: primaryColor3 = affordabilityCalculatorConfig.primaryColor,
  } = !data?.affordabilityCalculator
    ? affordabilityCalculatorConfig
    : data?.affordabilityCalculator

  /** Config object property mapping to incoming values from params for Closing Costs Calculator. */
  const {
    bg: bg4 = closingCostsCalculator.bg,
    primaryColor: primaryColor4 = closingCostsCalculator.primaryColor,
  } = !data?.closingCostsCalculator
    ? closingCostsCalculator
    : data?.closingCostsCalculator

  /** Config object property mapping to incoming values from params for banner widget. */
  const {
    style = bannerConfig.style,
    bg: bg5 = bannerConfig.bg,
    primaryColor: primaryColor5 = bannerConfig.primaryColor,
    font: font2 = bannerConfig.font,
    cta: cta2 = bannerConfig.cta,
    body = bannerConfig.body,
    link: link2,
  } = !data?.banner ? bannerConfig : data?.banner

  /** Config object property mapping to incoming values from params for mortgage app. */
  const { bg: bg6 = mortgageAppConfig.bg } = !data?.mortgageApp
    ? mortgageAppConfig
    : data?.mortgageApp

  /** Config object property mapping to incoming values from params for Closing Costs Calculator. */
  const {
    bg: bg7 = rentalOffsetCalculator.bg,
    primaryColor: primaryColor6 = rentalOffsetCalculator.primaryColor,
  } = !data?.rentalOffsetCalculator
    ? rentalOffsetCalculator
    : data?.rentalOffsetCalculator

  useEffect(() => {
    try {
      async function getSchema() {
        const res = await reqSchema()
        if (typeof res?.data === 'object') setSchema(res.data)
        else setSchema(false)
      }
      getSchema()
    } catch (e) {
      console.log('error fetching schema', e)
      setSchema(false)
    }
  }, [])

  const isValid = validate(schema, data, index)

  let partnerPath = `${data?.partner?.type}/${data?.partner?.code}`

  /** Returns true if an error exists on the param. */
  const checkValidity = (path) => {
    const res = isValid?.errors?.find((e) =>
      e?.dataPath?.split('/')?.includes(path)
    )
    return has(res, 'dataPath') && res?.dataPath?.split('/')?.includes(path)
  }

  /** Returns true if value is a hex color. */
  const isHexColor = (color) => {
    return /(^#[0-9A-F]{6}$)|(^#[0-9A-F]{3}$)/i.test(color)
  }

  if (
    typeof data?.partner?.type === 'undefined' ||
    typeof data?.partner?.code === 'undefined' ||
    checkValidity('partner')
  )
    partnerPath = ''

  return {
    productTable: {
      bg: !isHexColor(bg) ? '#FFF' : bg,
      primaryColor: !isHexColor(primaryColor) ? '#147bc9' : primaryColor,
      font: checkValidity('font') ? true : font,
      intro: checkValidity('intro') ? true : intro,
      cta: checkValidity('cta') ? 'Get This Mortgage' : cta,
      link: link ? link : `${config.APP}/${partnerPath}/`,
      rental: checkValidity('rental') ? 'no' : rental,
      mortgageType: checkValidity('mortgageType') ? 'purchase' : mortgageType,
      rateType: checkValidity('rateType') ? 'fixed' : rateType,
      province: checkValidity('province') ? 'ON' : province,
      filtered: {
        price: checkValidity('price') || price === undefined ? 1e6 : price,
        downPayment:
          checkValidity('downPayment') || downPayment === undefined
            ? 2e5
            : downPayment,
        balance:
          checkValidity('balance') || balance === undefined ? 13e4 : balance,
      },
      mixed: {
        price: checkValidity('price') || price === undefined ? 65e4 : price,
        downPayment:
          checkValidity('downPayment') || downPayment === undefined
            ? 9e4
            : downPayment,
        balance:
          checkValidity('balance') || balance === undefined ? 13e4 : balance,
      },
      lenderType: checkValidity('lenderType') ? 'any' : lenderType,
      term: checkValidity('term') ? 'any' : term,
      creditScore: checkValidity('creditScore') ? 'any' : creditScore,
      hide: checkValidity('hide') ? [] : hide || [],
      featureCount: checkValidity('featureCount') ? 3 : featureCount,
      lenderName: checkValidity('lenderName') ? [] : lenderName || [],
      style: checkValidity('style') ? 'table' : resultsStyle,
      linkParameters: checkValidity('linkParameters') ? {} : linkParameters || {},
      linkFragments: checkValidity('linkFragments') ? {} : linkFragments || {},
      accreditation: checkValidity('accreditation') ? true : accreditation,
      productName: checkValidity('productName') ? true : productName,
    },
    mortgagePaymentCalculator: {
      bg: !isHexColor(bg2) ? '#FFF' : bg2,
      primaryColor: !isHexColor(primaryColor2) ? '#147bc9' : primaryColor2,
    },
    affordabilityCalculator: {
      bg: !isHexColor(bg3) ? '#FFF' : bg3,
      primaryColor: !isHexColor(primaryColor3) ? '#147bc9' : primaryColor3,
    },
    closingCostsCalculator: {
      bg: !isHexColor(bg4) ? '#FFF' : bg4,
      primaryColor: !isHexColor(primaryColor4) ? '#147bc9' : primaryColor4,
    },
    rentalOffsetCalculator: {
      bg: !isHexColor(bg7) ? '#FFF' : bg7,
      primaryColor: !isHexColor(primaryColor6) ? '#147bc9' : primaryColor6,
    },
    banner: {
      style: checkValidity('style') ? 'homewise_1' : style,
      bg: !isHexColor(bg) ? '#FFF' : bg5,
      primaryColor: !isHexColor(primaryColor5) ? '#147bc9' : primaryColor5,
      font: checkValidity('font') ? true : font2,
      cta: checkValidity('cta') ? 'Get This Mortgage' : cta2,
      body: checkValidity('body')
        ? 'Complete a brief questionnaire to find your best mortgage option from over 30 lenders.'
        : body,
      link: link2 ? link2 : `${config.APP}/${partnerPath}/`,
    },
    mortgageApp: {
      bg: !isHexColor(bg6) ? '#FFF' : bg6,
    },
    partner: {
      type: data?.partner?.type || '',
      code: data?.partner?.code || '',
      segment: data?.partner?.segment || '',
    },
  }
}

/**
 * Creates a base64 encoded string that can be used with the `state` query parameter in HOA app.
 * @param {Object} data - { mortgageType: String, price: Number, downPayment: Number, borrowing: Number }
 * @returns {String} base64 encoded string
 */
export function createHoaState(data) {
  const app = {
    application: {
      financeType: {
        type: data?.mortgageType,
      },
      expectedPrice: {
        price: {
          value: data?.price,
          downPayment: {
            amount: data?.downPayment,
            percentage: isNaN(data?.downPayment / data?.price)
              ? 0
              : ((data?.downPayment / data?.price) * 100).toFixed(2),
          },
          borrowing: {
            amount: data?.borrowing,
          },
        },
      },
    },
  }
  return btoa(JSON.stringify(app))
}
